<template>
  <div class="product-attachment-upload">
    <a-upload
      :accept="accept"
      :disabled="disabled"
      :file-list="fileList"
      :list-type="kind === 'image' ? 'picture-card' : 'text'"
      :before-upload="beforeUpload"
      :custom-request="uploadFile"
      :remove="confirmRemove"
      @preview="previewFile">
      <div v-if="!disabled && fileList.length < 1">
        <template v-if="kind === 'image'">
          <a-icon :type="uploading ? 'loading' : 'plus'" />
          <div class="upload-label">{{ uploading ? '上传中' : '上传图片' }}</div>
        </template>
        <a-button v-else :loading="uploading" icon="upload">{{ uploadLabel }}</a-button>
      </div>
    </a-upload>
    <video v-if="kind === 'video' && previewUrl" class="attachment-video" :src="previewUrl" controls preload="metadata" />
    <small>{{ hint }}</small>
    <a-modal
      v-if="kind === 'image'"
      :visible="imagePreviewVisible"
      :footer="null"
      centered
      width="auto"
      wrap-class-name="product-image-preview-modal"
      destroy-on-close
      @cancel="closeImagePreview">
      <img v-if="imagePreviewUrl" class="product-image-preview-full" :src="imagePreviewUrl" alt="商品图片预览">
    </a-modal>
  </div>
</template>

<script>
import { uploadProductAttachment } from '@/api/erp/product'

export default {
  name: 'ProductAttachmentUpload',
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: String, default: '' },
    kind: { type: String, default: 'file' },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return {
      fileList: [],
      uploading: false,
      localPreview: '',
      imagePreviewVisible: false,
      imagePreviewUrl: ''
    }
  },
  computed: {
    accept () {
      if (this.kind === 'image') return '.jpg,.jpeg,.png,.webp'
      if (this.kind === 'video') return '.mp4,.mov'
      return '.jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx'
    },
    uploadLabel () { return this.kind === 'video' ? '上传视频' : '上传报告' },
    hint () {
      if (this.kind === 'image') return '格式要求1:1，>750*750大小'
      if (this.kind === 'video') return '支持 MP4 / MOV，单个不超过10MB'
      return '支持图片、PDF、Word、Excel，单个不超过10MB'
    },
    previewUrl () {
      return this.localPreview || (this.fileList[0] && this.fileList[0].url) || ''
    }
  },
  beforeDestroy () {
    this.revokeLocalPreview()
  },
  watch: {
    value: { immediate: true, handler (value) { this.syncFromValue(value) } }
  },
  methods: {
    syncFromValue (value) {
      const url = this.toPublicUrl(value)
      if (!url) {
        this.revokeLocalPreview()
        this.fileList = []
        return
      }
      if (this.fileList.length === 1 && this.fileList[0].url === url) return
      const thumbUrl = this.kind === 'image' && this.localPreview ? this.localPreview : ''
      this.fileList = [{
        uid: `saved-${url}`,
        name: this.fileName(url),
        status: 'done',
        url,
        thumbUrl,
        type: this.kind === 'image' ? 'image/*' : (this.kind === 'video' ? 'video/mp4' : '')
      }]
    },
    toPublicUrl (url) {
      const text = String(url || '').trim()
      if (!text || text.indexOf('blob:') === 0 || text.indexOf('data:') === 0) return text
      const marker = '/local/file/'
      const index = text.indexOf(marker)
      if (index < 0) return text
      return '/admin-api' + text.substring(index)
    },
    revokeLocalPreview () {
      if (this.localPreview) {
        URL.revokeObjectURL(this.localPreview)
        this.localPreview = ''
      }
    },
    fileName (url) {
      const clean = String(url || '').split('?')[0]
      const name = clean.substring(clean.lastIndexOf('/') + 1)
      try { return decodeURIComponent(name) || '附件' } catch (e) { return name || '附件' }
    },
    beforeUpload (file) {
      const ext = String(file.name || '').split('.').pop().toLowerCase()
      const allowed = this.accept.split(',').map(item => item.replace('.', ''))
      if (!allowed.includes(ext)) { this.$message.error('文件类型不符合当前上传要求'); return false }
      const max = 10
      if (file.size > max * 1024 * 1024) { this.$message.error(`单个文件不能超过${max}MB`); return false }
      return true
    },
    uploadFile ({ file, onSuccess, onError }) {
      const data = new FormData()
      data.append('file', file)
      this.revokeLocalPreview()
      this.localPreview = URL.createObjectURL(file)
      this.uploading = true
      uploadProductAttachment(data).then(res => {
        const url = this.toPublicUrl(res && res.data)
        const thumbUrl = this.kind === 'image' ? this.localPreview : ''
        const uid = file.uid || ('upload-' + Date.now())
        this.fileList = [{ uid, name: file.name, status: 'done', url, thumbUrl, type: file.type || '' }]
        this.$emit('input', url)
        onSuccess(res, file)
      }).catch(error => {
        this.$message.error((error && error.message) || '文件上传失败')
        onError(error)
      }).finally(() => { this.uploading = false })
    },
    confirmRemove () {
      if (this.disabled) return false
      return new Promise(resolve => {
        this.$confirm({
          title: '移除附件',
          content: '仅在保存商品后解除当前档案关联，已上传的原文件不会被立即物理删除。确定移除吗？',
          okType: 'danger',
          onOk: () => { this.revokeLocalPreview(); this.fileList = []; this.$emit('input', ''); resolve(true) },
          onCancel: () => resolve(false)
        })
      })
    },
    closeImagePreview () {
      this.imagePreviewVisible = false
      this.imagePreviewUrl = ''
    },
    previewFile (file) {
      const url = (file && (file.url || file.thumbUrl)) || this.previewUrl
      if (!url) return
      if (this.kind === 'image') {
        this.imagePreviewUrl = url
        this.imagePreviewVisible = true
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }
}
</script>

<style scoped>
.product-attachment-upload small{display:block;margin-top:6px;color:#8a94a3;line-height:1.5}.upload-label{margin-top:5px;font-size:12px}.product-attachment-upload /deep/ .ant-upload-list-item-name{max-width:320px}
.product-attachment-upload /deep/ .ant-upload-list-picture-card .ant-upload-list-item-thumbnail img{width:100%;height:100%;object-fit:contain}
.attachment-video{display:block;width:min(360px,100%);height:200px;margin-top:8px;background:#000;border-radius:6px}
.product-image-preview-full{display:block;max-width:min(88vw,960px);max-height:78vh;margin:0 auto;object-fit:contain}
</style>
